import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Calendar,
  Clock,
  Focus,
  Globe,
  LayoutDashboard,
  MessageSquare,
  Plus,
  Settings,
  ShoppingBag,
  User,
  UserPlus,
  Users,
  Wand2,
} from 'lucide-react';
import { Box } from '@mui/material';
import { AppModal } from '../AppModal';
import { AppTextField } from '../AppTextField';
import { useBusinessStore, usePreferencesStore } from '@/stores';
import { colors, spacing, radius, fonts } from '@/theme/tokens';
import { can, type Capability } from '@tilly/shared';

type CommandGroup = 'create' | 'navigate' | 'preferences';

interface Command {
  id: string;
  group: CommandGroup;
  label: string;
  icon: ReactNode;
  requiredCapability?: Capability;
  run: () => void;
}

interface CommandActionPaletteProps {
  open: boolean;
  onClose: () => void;
  onNewClient: () => void;
}

const GROUP_ORDER: CommandGroup[] = ['create', 'navigate', 'preferences'];

export function CommandActionPalette({ open, onClose, onNewClient }: CommandActionPaletteProps) {
  const { t } = useTranslation(['admin', 'common']);
  const navigate = useNavigate();
  const activeRole = useBusinessStore((s) => s.activeRole);
  const { focusMode, setFocusMode, reduceMotion, setReduceMotion } = usePreferencesStore();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setQuery('');
      setSelectedIndex(0);
    }
  }

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  const commands: Command[] = useMemo(
    () => [
      {
        id: 'new-appointment',
        group: 'create',
        label: t('admin:commandPalette.commands.newAppointment'),
        icon: <Plus size={14} />,
        requiredCapability: 'appointments.manage',
        run: () => navigate('/admin/calendar?newAppointment=1'),
      },
      {
        id: 'new-client',
        group: 'create',
        label: t('admin:commandPalette.commands.newClient'),
        icon: <UserPlus size={14} />,
        requiredCapability: 'clients.manage',
        run: onNewClient,
      },
      {
        id: 'go-dashboard',
        group: 'navigate',
        label: t('common:nav.dashboard'),
        icon: <LayoutDashboard size={14} />,
        run: () => navigate('/admin/dashboard'),
      },
      {
        id: 'go-calendar',
        group: 'navigate',
        label: t('common:nav.calendar'),
        icon: <Calendar size={14} />,
        requiredCapability: 'appointments.view',
        run: () => navigate('/admin/calendar'),
      },
      {
        id: 'go-schedule',
        group: 'navigate',
        label: t('common:nav.mySchedule'),
        icon: <Clock size={14} />,
        requiredCapability: 'schedule.self.manage',
        run: () => navigate('/admin/schedule'),
      },
      {
        id: 'go-clients',
        group: 'navigate',
        label: t('common:nav.clients'),
        icon: <Users size={14} />,
        requiredCapability: 'clients.view',
        run: () => navigate('/admin/clients'),
      },
      {
        id: 'go-messages',
        group: 'navigate',
        label: t('common:nav.messages'),
        icon: <MessageSquare size={14} />,
        requiredCapability: 'messages.view',
        run: () => navigate('/admin/messages'),
      },
      {
        id: 'go-providers',
        group: 'navigate',
        label: t('common:nav.providers'),
        icon: <Users size={14} />,
        requiredCapability: 'team.manage',
        run: () => navigate('/admin/providers'),
      },
      {
        id: 'go-catalog',
        group: 'navigate',
        label: t('common:nav.catalog'),
        icon: <ShoppingBag size={14} />,
        requiredCapability: 'services.manage',
        run: () => navigate('/admin/catalog'),
      },
      {
        id: 'go-booking-page',
        group: 'navigate',
        label: t('common:nav.bookingPage'),
        icon: <Globe size={14} />,
        requiredCapability: 'business.settings.manage',
        run: () => navigate('/admin/booking-page'),
      },
      {
        id: 'go-reports',
        group: 'navigate',
        label: t('common:nav.reports'),
        icon: <BarChart3 size={14} />,
        requiredCapability: 'reports.view',
        run: () => navigate('/admin/reports'),
      },
      {
        id: 'go-settings',
        group: 'navigate',
        label: t('common:nav.settings'),
        icon: <Settings size={14} />,
        requiredCapability: 'business.settings.manage',
        run: () => navigate('/admin/settings'),
      },
      {
        id: 'go-profile',
        group: 'navigate',
        label: t('common:nav.profile'),
        icon: <User size={14} />,
        run: () => navigate('/admin/profile'),
      },
      {
        id: 'toggle-focus-mode',
        group: 'preferences',
        label: focusMode
          ? t('admin:commandPalette.commands.disableFocusMode')
          : t('admin:commandPalette.commands.enableFocusMode'),
        icon: <Focus size={14} />,
        run: () => setFocusMode(!focusMode),
      },
      {
        id: 'toggle-reduce-motion',
        group: 'preferences',
        label: reduceMotion
          ? t('admin:commandPalette.commands.disableReduceMotion')
          : t('admin:commandPalette.commands.enableReduceMotion'),
        icon: <Wand2 size={14} />,
        run: () => setReduceMotion(!reduceMotion),
      },
    ],
    [t, navigate, onNewClient, focusMode, setFocusMode, reduceMotion, setReduceMotion]
  );

  const visibleCommands = useMemo(
    () =>
      commands.filter(
        (cmd) => !cmd.requiredCapability || (activeRole && can(activeRole, cmd.requiredCapability))
      ),
    [commands, activeRole]
  );

  const filteredCommands = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return visibleCommands;
    return visibleCommands.filter((cmd) => cmd.label.toLowerCase().includes(q));
  }, [visibleCommands, query]);

  const groupedCommands = useMemo(() => {
    const groups = new Map<CommandGroup, Command[]>();
    for (const cmd of filteredCommands) {
      const existing = groups.get(cmd.group) ?? [];
      existing.push(cmd);
      groups.set(cmd.group, existing);
    }
    return GROUP_ORDER.filter((g) => groups.has(g)).map((group) => ({
      group,
      items: groups.get(group)!,
    }));
  }, [filteredCommands]);

  const indexById = useMemo(() => {
    const map = new Map<string, number>();
    filteredCommands.forEach((cmd, index) => map.set(cmd.id, index));
    return map;
  }, [filteredCommands]);

  const clampedSelectedIndex =
    filteredCommands.length === 0 ? -1 : Math.min(selectedIndex, filteredCommands.length - 1);

  function runCommand(cmd: Command) {
    cmd.run();
    onClose();
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (filteredCommands.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % filteredCommands.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredCommands.length) % filteredCommands.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selected = filteredCommands[clampedSelectedIndex];
      if (selected) runCommand(selected);
    }
  }

  return (
    <AppModal open={open} onClose={onClose} size="lg" hideCloseButton>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: spacing[3] }}>
        <AppTextField
          inputRef={inputRef}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('admin:commandPalette.placeholder')}
          autoFocus
          role="combobox"
          aria-expanded={filteredCommands.length > 0}
          // eslint-disable-next-line i18next/no-literal-string -- DOM id reference, not translatable text
          aria-controls="command-action-palette-listbox"
          aria-activedescendant={
            clampedSelectedIndex >= 0 && filteredCommands[clampedSelectedIndex]
              ? `command-action-option-${filteredCommands[clampedSelectedIndex].id}`
              : undefined
          }
        />

        <Box
          id="command-action-palette-listbox"
          role="listbox"
          sx={{ maxHeight: '420px', overflowY: 'auto' }}
        >
          {filteredCommands.length === 0 && (
            <Box sx={{ padding: spacing[4], color: colors.textMuted, fontSize: '14px' }}>
              {query.trim()
                ? t('admin:commandPalette.noResults', { query: query.trim() })
                : t('admin:commandPalette.hint')}
            </Box>
          )}

          {groupedCommands.map((groupEntry) => (
            <Box key={groupEntry.group} sx={{ marginBottom: spacing[2] }}>
              <Box
                sx={{
                  padding: `${spacing[2]} ${spacing[3]}`,
                  color: colors.textMuted,
                  fontSize: '12px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                }}
              >
                {t(`admin:commandPalette.groups.${groupEntry.group}`)}
              </Box>
              {groupEntry.items.map((cmd) => {
                const itemIndex = indexById.get(cmd.id) ?? -1;
                const isSelected = itemIndex === clampedSelectedIndex;
                return (
                  <Box
                    key={cmd.id}
                    id={`command-action-option-${cmd.id}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setSelectedIndex(itemIndex)}
                    onClick={() => runCommand(cmd)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: spacing[3],
                      padding: `${spacing[2]} ${spacing[3]}`,
                      borderRadius: radius.md,
                      cursor: 'pointer',
                      backgroundColor: isSelected ? colors.accentLighter : 'transparent',
                      color: colors.textMuted,
                    }}
                  >
                    {cmd.icon}
                    <Box
                      sx={{
                        fontFamily: fonts.body,
                        fontSize: '14px',
                        fontWeight: 500,
                        color: colors.textPrimary,
                      }}
                    >
                      {cmd.label}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>
    </AppModal>
  );
}
